import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  PetlingsLogo,
  PersonalityIcon,
  CosmeticsIcon,
  ChatIcon,
  KennelIcon,
  PetCountIcon,
  SyncIcon,
  LeafIcon,
  EmoteIcon,
  FoodCountIcon,
  AbilityIcon,
  DressedPetIcon,
  LiveEmoteIcon,
  PeerSyncIcon,
  AnyServerIcon,
  PortalIcon,
} from '../components/icons';
import { PetVisual, petName, PETS, usePetSelection } from '../components/visuals';
import MeetThePets from '../components/meet/MeetThePets';
import DownloadDropdown from '../components/DownloadDropdown';
import ReleaseHero from '../components/release/ReleaseHero';
import PetDivider from '../components/PetDivider';
import ServerShowcase from '../components/servers/ServerShowcase';
import FutureWorlds from '../components/future/FutureWorlds';
import './Home.css';

/** A few of the forty-two, beside the screenshots. */
const SHOWCASE_PETS = ['cat', 'wolf', 'fox', 'panda', 'axolotl', 'slime', 'baby_dragon'];

const SCREENSHOTS = [
  { src: '/images/pet-playground.webp', alt: 'Pets playing in Minecraft' },
  { src: '/images/tamagotchi-screen.webp', alt: 'Pet care screen' },
  { src: '/images/kennel-preview.webp', alt: 'Pet kennel management' },
  { src: '/images/pet-showcase-concept.webp', alt: 'Concept art', concept: true },
];

const FEATURES = [
  { Icon: PetCountIcon, label: '42 Pets', color: '#AB47BC' },
  { Icon: CosmeticsIcon, label: '150+ Cosmetics', color: 'var(--magenta-primary)' },
  { Icon: EmoteIcon, label: '19 Emotes', color: '#42A5F5' },
  { Icon: FoodCountIcon, label: '12 Foods', color: '#FF8A65' },
  { Icon: AbilityIcon, label: '6 Abilities', color: 'var(--orange-primary)' },
  { Icon: SyncIcon, label: 'Cross-Platform', color: 'var(--yellow-warm)' },
];

function AppleIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="currentColor">
      <path d="M14.2 9.4c0-2.1 1.7-3.1 1.8-3.2-1-1.5-2.5-1.7-3-1.7-1.3-.1-2.5.8-3.1.8-.7 0-1.7-.7-2.8-.7-1.4 0-2.7.8-3.5 2.1C2.1 9.2 3 13 4.5 15c.7 1 1.6 2.2 2.7 2.1 1.1 0 1.5-.7 2.8-.7 1.3 0 1.6.7 2.8.7 1.2 0 1.9-1 2.7-2.1.8-1.2 1.2-2.4 1.2-2.4-.1-.1-2.5-1-2.5-3.2zM11.9 3.3c.6-.7 1-1.7.9-2.7-1 0-2.1.6-2.8 1.4-.6.7-1.1 1.7-1 2.7 1 .1 2.1-.6 2.9-1.4z" />
    </svg>
  );
}

function MinecraftBlockIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
      <rect x="2" y="6" width="16" height="12" rx="1" fill="#8B6940" />
      <rect x="2" y="2" width="16" height="6" rx="1" fill="#5D8C3E" />
      <rect x="5" y="3" width="3" height="2" rx="0.5" fill="#4A7030" />
      <rect x="11" y="4" width="2" height="1.5" rx="0.5" fill="#4A7030" />
      <rect x="4" y="8" width="3" height="3" rx="0.5" fill="#7A5C35" />
      <rect x="9" y="10" width="2" height="2" rx="0.5" fill="#7A5C35" />
      <rect x="13" y="8" width="3" height="2" rx="0.5" fill="#7A5C35" />
    </svg>
  );
}

/** The Hollow's season: late September through October. */
function hollowSeason(now = new Date()) {
  const m = now.getMonth() + 1, d = now.getDate();
  return m === 10 || (m === 9 && d >= 20);
}

export default function Home() {
  const isHollowSeason = hollowSeason();
  const showcasePets = usePetSelection(SHOWCASE_PETS);
  const [screenshotIndex, setScreenshotIndex] = useState(0);
  const videoRef = useRef(null);
  const heroVisualRef = useRef(null);

  // Drive the page-side sun glow off the video's currentTime so it
  // stays locked to the video's actual sun position regardless of when
  // the video loads, pauses, or loops. Updates --sun-progress (0-1)
  // and --sun-opacity on .hero-visual, which the CSS reads.
  useEffect(() => {
    const video = videoRef.current;
    const hero = heroVisualRef.current;
    if (!video || !hero) return;

    let rafId = null;
    const update = () => {
      const dur = video.duration;
      if (dur && dur > 0) {
        const progress = (video.currentTime / dur) % 1;
        // Position 0->1 maps to translateX -100% -> +100% (off-screen each end)
        hero.style.setProperty('--sun-progress', progress.toFixed(4));
        // Trapezoid fade: full opacity in the middle 60%, fades at outer 20% each side
        const x = progress * 2 - 1;
        const absX = Math.abs(x);
        const opacity = absX <= 0.4 ? 1 : absX >= 1 ? 0 : (1 - absX) / 0.6;
        hero.style.setProperty('--sun-opacity', opacity.toFixed(3));
      }
      rafId = requestAnimationFrame(update);
    };
    rafId = requestAnimationFrame(update);
    return () => { if (rafId) cancelAnimationFrame(rafId); };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setScreenshotIndex((prev) => (prev + 1) % SCREENSHOTS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const currentShot = SCREENSHOTS[screenshotIndex];

  return (
    <div className="home">
      {/* SVG magenta-to-alpha filter: alpha = 1.2*R + 1.2*B - 2.8*G - 0.1
          The -2.8 G coefficient kills warm spotlit bg pixels even at
          peak brightness (R+G high but B low = orange/cream = neutral
          minus green wins). Magenta pet (R+B heavy, low G) stays opaque
          because R+B sum dominates the green subtraction. */}
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <filter id="petlings-kill-bg" colorInterpolationFilters="sRGB">
            <feColorMatrix
              type="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  1.2 -2.8 1.2 0 -0.1"
            />
            <feComponentTransfer>
              <feFuncA type="linear" slope="2.0" intercept="-0.15" />
            </feComponentTransfer>
          </filter>
        </defs>
      </svg>

      {/* Hero */}
      <section className="hero">
        <div className="container hero-inner">
          <div className="hero-content">
            <img
              src="/branding/petlings-wordmark-600.png"
              srcSet="/branding/petlings-wordmark-600.png 1x, /branding/petlings-wordmark-1200.png 2x"
              alt="Petlings"
              className="hero-wordmark"
              draggable="false"
            />
            <h1 className="hero-title">
              Your tiny companion,<br />
              <span className="hero-highlight">in every world.</span>
            </h1>
            <p className="hero-subtitle">
              Adopt, dress up, and care for AI-powered pet companions
              that follow you through Minecraft. They have personalities,
              remember your conversations, and grow alongside you.
            </p>

            <div className="hero-downloads">
              <DownloadDropdown className="download-btn download-minecraft">
                <MinecraftBlockIcon size={22} />
                <div className="download-btn-text">
                  <span className="download-btn-label">Download for</span>
                  <span className="download-btn-platform">Minecraft</span>
                </div>
              </DownloadDropdown>
              <a href="#" className="download-btn download-appstore" target="_blank" rel="noopener noreferrer">
                <AppleIcon size={22} />
                <div className="download-btn-text">
                  <span className="download-btn-label">Download on the</span>
                  <span className="download-btn-platform">App Store</span>
                </div>
              </a>
            </div>

            <div className="hero-actions-secondary">
              <Link to="/catalog" className="btn btn-ghost">Meet the Pets</Link>
              <Link to="/about" className="btn btn-ghost">Learn More</Link>
            </div>
          </div>
          <div className="hero-visual" ref={heroVisualRef}>
            <video
              ref={videoRef}
              className="hero-pet-video"
              autoPlay
              muted
              loop
              playsInline
              poster="/branding/petlings-pet-512.png"
              aria-label="Petlings voxel pet"
            >
              <source src="/branding/petlings-pet-loop.webm" type="video/webm" />
              <source src="/branding/petlings-pet-loop.mp4" type="video/mp4" />
              <img src="/branding/petlings-pet-512.png" alt="Petlings voxel pet" />
            </video>
          </div>
        </div>
      </section>

      {/* A season's free garden, said while it is on: October is the Hollow's. */}
      {isHollowSeason && (
        <section className="season-note">
          <div className="container">
            <div className="season-card">
              <span className="season-badge">Free event</span>
              <div>
                <h3>Pumpkin Hollow is free this October</h3>
                <p>
                  A garden of your own under the harvest moon: lanterns, a hay maze, friendly ghosts that lead
                  the way to treasure and leave sweets after dark. In the Petlings app, Gardens &rarr; New garden.
                  Costs nothing in October, and it is yours for good.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Heart Tree update showcase */}
      <ReleaseHero />

      <PetDivider />

      {/* The forty-two, in their new voxel look */}
      <MeetThePets />

      {/* Feature strip - compact */}
      <section className="feature-strip">
        <div className="container">
          <div className="feature-strip-items">
            {FEATURES.map(({ Icon, label, color }) => (
              <div key={label} className="feature-strip-item">
                <Icon size={22} color={color} />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshots + Companion showcase - compact combined section */}
      <section className="showcase-section">
        <div className="container">
          <div className="showcase-split">
            {/* Left: auto-rotating screenshot card */}
            <div className="showcase-screenshots">
              <div className="screenshot-card" key={screenshotIndex}>
                <img src={currentShot.src} alt={currentShot.alt} />
                {currentShot.concept && <span className="screenshot-concept">AI concept</span>}
              </div>
              <div className="screenshot-dots">
                {SCREENSHOTS.map((_, i) => (
                  <button key={i} className={`screenshot-dot ${i === screenshotIndex ? 'active' : ''}`}
                          onClick={() => setScreenshotIndex(i)} />
                ))}
              </div>
            </div>

            {/* Right: pet carousel + highlights */}
            <div className="showcase-info">
              <h2>42 companions,<br /><span className="hero-highlight">endless adventures.</span></h2>
              <p>
                Each pet has its own personality, mood, and needs. Feed them,
                play with them, dress them up with 3D cosmetics, and chat with them
                using AI. They remember your journeys and grow alongside you.
              </p>
              <div className="showcase-pets-mini">
                {showcasePets.map((id) => (
                  <div key={id} className="showcase-pet-mini" title={petName(id)}>
                    <PetVisual petId={id} size={40} />
                  </div>
                ))}
                <Link to="/catalog" className="showcase-pet-mini showcase-pet-more">
                  +{PETS.length - SHOWCASE_PETS.length}
                </Link>
              </div>
              <Link to="/catalog" className="btn btn-secondary">Browse All Pets</Link>
            </div>
          </div>
        </div>
      </section>

      <PetDivider />

      {/* How it works - compact 3-column with inline screenshots */}
      <section className="how-section">
        <div className="container">
          <h2 className="section-title">How it works</h2>
          <div className="how-grid">
            <div className="how-card">
              <div className="how-card-visual">
                <div className="how-card-icon">
                  <CosmeticsIcon size={28} color="var(--green-primary)" />
                </div>
              </div>
              <h3>Dress up in 3D</h3>
              <p>Hats, glasses, capes, boots. 3D cosmetics in Minecraft, 2D editor on Web and iOS.</p>
            </div>
            <div className="how-card">
              <div className="how-card-visual">
                <div className="how-card-icon">
                  <ChatIcon size={28} color="#42A5F5" />
                </div>
              </div>
              <h3>Chat with AI</h3>
              <p>Your pet gives crafting tips, remembers your travels, and plans adventures. Each has a unique personality.</p>
            </div>
            <div className="how-card">
              <div className="how-card-visual">
                <div className="how-card-icon">
                  <SyncIcon size={28} color="var(--orange-primary)" />
                </div>
              </div>
              <h3>Sync everywhere</h3>
              <p>Add a hat from your phone, it appears in-game instantly. Chat from the web between sessions. Everything syncs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Multiplayer */}
      <section className="mp-section">
        <div className="container">
          <div className="mp-layout">
            {/* SVG illustration: players with different pets */}
            <div className="mp-illustration">
              <img
                src="/images/every-server.webp"
                alt="Floating voxel worlds joined by glowing portals, with players and their pets crossing between them"
                className="mp-scene"
                width="1216"
                height="816"
                loading="lazy"
              />
            </div>

            {/* Info */}
            <div className="mp-info">
              <h2>Works on every server.</h2>
              <p>
                Petlings is fully multiplayer. Join any server and your pet comes with you.
                Other players with the mod see your pet, your cosmetics, your emotes, everything.
              </p>
              <div className="mp-features">
                <div className="mp-feat">
                  <span className="mp-feat-icon" aria-hidden="true"><DressedPetIcon size={22} color="var(--magenta-primary)" /></span>
                  <span>See other players' dressed-up pets</span>
                </div>
                <div className="mp-feat">
                  <span className="mp-feat-icon" aria-hidden="true"><LiveEmoteIcon size={22} color="#42A5F5" /></span>
                  <span>Watch friends trigger emotes in real time</span>
                </div>
                <div className="mp-feat">
                  <span className="mp-feat-icon" aria-hidden="true"><PeerSyncIcon size={22} color="var(--orange-primary)" /></span>
                  <span>Low-latency peer sync, no server plugins needed</span>
                </div>
                <div className="mp-feat">
                  <span className="mp-feat-icon" aria-hidden="true"><AnyServerIcon size={22} color="#5D8C3E" /></span>
                  <span>Works on any Minecraft server, vanilla or modded</span>
                </div>
                <div className="mp-feat">
                  <span className="mp-feat-icon" aria-hidden="true"><PortalIcon size={22} color="#9C4DFF" /></span>
                  <span>More worlds on the way: see Future worlds below</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chat preview - compact inline */}
      <section className="chat-teaser">
        <div className="container">
          <div className="chat-teaser-layout">
            <div className="chat-teaser-bubbles">
              <div className="ct-bubble ct-user">How do I find a nether fortress?</div>
              <div className="ct-bubble ct-pet">
                <span className="ct-name">Whiskers</span>
                Travel along the Z axis in the Nether! I remember we found one near that lava lake yesterday.
              </div>
              <div className="ct-bubble ct-user">Let's go!</div>
              <div className="ct-bubble ct-pet">
                <span className="ct-name">Whiskers</span>
                Adventure time! I'll keep my eyes peeled for blaze spawners.
              </div>
            </div>
            <div className="chat-teaser-info">
              <h3>A smart companion that knows you.</h3>
              <p className="chat-teaser-desc">Your pet learns how you play, knows your surroundings, and remembers every adventure you've shared.</p>
              <div className="chat-teaser-tags">
                <span>Knows your biome</span>
                <span>Crafting help</span>
                <span>Remembers travels</span>
                <span>Plans adventures</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sister servers: RevivalSMP and VanillaTown, sliding between them */}
      <ServerShowcase />

      {/* Where Petlings might go next: plans, not partnerships */}
      <FutureWorlds />

      {/* Learn more CTA */}
      <section className="learn-more">
        <div className="container learn-more-inner">
          <img src="/branding/petlings-pet-128.png" alt="" className="learn-more-mark" width={64} height={64} />
          <h3>Want to know more?</h3>
          <p>Learn how Petlings works, what makes each pet unique, and how the cross-platform magic happens.</p>
          <Link to="/about" className="btn btn-primary">Read the Full Story</Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container footer-inner">
          <div className="footer-brand">
            <img
              src="/branding/petlings-wordmark-300.png"
              srcSet="/branding/petlings-wordmark-300.png 1x, /branding/petlings-wordmark-600.png 2x"
              alt="Petlings"
              className="footer-wordmark"
              draggable="false"
            />
          </div>
          <p className="footer-text">Tiny companions, big adventures.</p>
          <p className="footer-disclaimer">Not an official Minecraft product. Not approved by or associated with Mojang or Microsoft.</p>
          <div className="footer-links">
            <Link to="/about">About</Link>
            <span className="footer-sep">&middot;</span>
            <Link to="/support">Support</Link>
            <span className="footer-sep">&middot;</span>
            <Link to="/privacy">Privacy</Link>
            <span className="footer-sep">&middot;</span>
            <Link to="/terms">Terms</Link>
            <span className="footer-sep">&middot;</span>
            <a href="https://discord.gg/hkcNBCTckG" target="_blank" rel="noopener noreferrer">Discord</a>
            <span className="footer-sep">&middot;</span>
            <a href="https://www.reddit.com/r/petlings" target="_blank" rel="noopener noreferrer">Reddit</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
